import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormStep, IAutoQuestionFormData } from '@employer/app/components/jobs-automation/automation-auto-question/auto-questions.model'
import { AutoQuestionStore } from '@employer/app/components/jobs-automation/automation-auto-question/auto-questions.store'
import { AutoQuestionModalTabComponent } from '@employer/app/components/jobs-automation/automation-auto-question/partials/auto-question-modal-tab.component'
import { JobDefaultAutomationStore } from '@employer/app/modules/jobs/views/job-automation/job-default-automation.store'
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button'
import { E11ModalGlobalService } from '@engineering11/ui-lib/e11-modal'
import { E11TabComponent, E11TabsComponent, E11TabsModule } from '@engineering11/ui-lib/e11-tabs'
import { E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils'
import { ViewportService } from '@engineering11/web-utilities'
import { LetDirective } from '@ngrx/component'
import { TranslateModule } from '@ngx-translate/core'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { AutomatedQuestionInterviewFormComponent } from './automated-question-interview-form'
import { AutomatedQuestionMessagesFormComponent } from './automated-question-messages-form'
import { AutomatedQuestionNameFormComponent } from './automated-question-name-form/automated-question-name-form.component'
import { AutomatedQuestionTriggerFormComponent } from './automated-question-trigger/automated-question-trigger-form.component'
import { NgIf } from '@angular/common'

@Component({
  selector: 'automated-question-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AutomatedQuestionTriggerFormComponent,
    AutomatedQuestionInterviewFormComponent,
    AutomatedQuestionMessagesFormComponent,
    E11ButtonModule,
    LetDirective,
    E11TabsModule,
    NgIf,
    E11UtilModule,
    TranslateModule,
    AutoQuestionModalTabComponent,
    AutomatedQuestionNameFormComponent,
  ],
  template: `
    <ng-container *ngrxLet="formData$ as formData">
      <ng-container *ngrxLet="defaultMessage$ as defaultMessage">
        <ng-template #content>
          <div class="e11-bg-skin-white e11-gap-4 e11-flex e11-flex-col md:e11-flex-row e11-justify-between">
            <div class="e11-flex-1">
              <h3 class="e11-mb-2 e11-skeleton-empty" *ngIf="jobTitle">{{ jobTitle }}</h3>
              <p class="e11-mb-0 e11-skeleton-empty" *ngIf="jobLocation">{{ jobLocation }}</p>
            </div>
            <div class="e11-w-full md:e11-w-1/3">
              <automated-question-name-form #nameFormComponent [formData]="formData"></automated-question-name-form>
            </div>
          </div>
          <div class="e11-mb-4">
            @defer {
            <automated-question-trigger #triggerFormComponent [formData]="formData"></automated-question-trigger>
            }
          </div>
          <div class="e11-border e11-border-t-0 e11-border-skin-primary-accent e11-rounded-lg">
            <e11-tabs
              #tabsComponent
              [tabClassOverrides]="'!e11-mr-0 e11-text-lg e11-border-none e11-flex-1'"
              (selectedTabChanged)="selectedTabChanged($event)"
            >
              <e11-tab #questionTab [title]="'Questions'" [tabId]="FormStep.Question">
                <ng-template e11Template="customHeader">
                  <auto-question-modal-tab
                    [isActive]="questionTab.active"
                    [title]="questionTab.title"
                    [isFirst]="true"
                    [hasSibling]="true"
                    [icon]="'quiz'"
                  >
                    {{ 'Write or select the question(s) to be sent to the candidate.' | translate }}
                  </auto-question-modal-tab>
                </ng-template>
                <ng-template e11Template="tabContent">
                  @defer {
                  <div class="e11-p-4">
                    <automated-question-interview-form #questionsFormComponent [formData]="formData"></automated-question-interview-form>
                  </div>
                  }
                </ng-template>
              </e11-tab>
              <e11-tab #messagesTab [tabId]="FormStep.Messages" [title]="'Messages'">
                <ng-template e11Template="customHeader">
                  <auto-question-modal-tab [isActive]="messagesTab.active" [title]="messagesTab.title" [isLast]="true" [icon]="'comment'">
                    {{ 'Customize the messages that inform candidates they have questions to answer.' | translate }}
                  </auto-question-modal-tab>
                </ng-template>
                <ng-template e11Template="tabContent">
                  @defer {
                  <div class="e11-p-4">
                    <automated-question-messages-form
                      #messagesFormComponent
                      [formData]="formData"
                      [defaultMessage]="defaultMessage"
                    ></automated-question-messages-form>
                  </div>
                  }
                </ng-template>
              </e11-tab>
            </e11-tabs>
          </div>
        </ng-template>
        <ng-template #footer>
          <div class="e11-flex e11-justify-end">
            <e11-button (click)="onSave(formData)" color="primary-accent" size="sm" [value]="'Save Automated Questions'"></e11-button>
          </div>
        </ng-template>
      </ng-container>
    </ng-container>
  `,
  styles: ``,
})
export class AutomatedQuestionModalComponent implements OnInit, OnDestroy {
  @ViewChild('content') content!: TemplateRef<any>
  @ViewChild('footer') footer!: TemplateRef<any>
  @ViewChild('tabsComponent') tabsComponent!: E11TabsComponent
  @ViewChild('nameFormComponent') nameFormComponent!: AutomatedQuestionNameFormComponent
  @ViewChild('triggerFormComponent') triggerFormComponent!: AutomatedQuestionTriggerFormComponent
  @ViewChild('questionsFormComponent') questionsFormComponent!: AutomatedQuestionInterviewFormComponent
  @ViewChild('messagesFormComponent') messagesFormComponent!: AutomatedQuestionMessagesFormComponent
  @Input() jobDetail!: { jobTitle: string; locationAddress: { city?: string; state?: string; [key: string]: any } }

  @Output() onSavedChanges = new EventEmitter<void>()
  FormStep = FormStep
  creationMode = false
  selectedTabId: FormStep = FormStep.Trigger

  destroyed$: Subject<boolean> = new Subject<boolean>()
  isMobile: boolean = false

  formData$ = this.autoQuestionStore.formData$
  defaultMessage$ = this.jobDefaultAutomationStore.defaultMessage$

  constructor(
    private modalService: E11ModalGlobalService,
    private autoQuestionStore: AutoQuestionStore,
    private jobDefaultAutomationStore: JobDefaultAutomationStore,
    private viewportService: ViewportService,
    private cdr: ChangeDetectorRef
  ) {
    this.resetCurrentAutomationOnClose()
    this.jobDefaultAutomationStore.onGetAutomationForJobPost()
  }

  ngOnInit(): void {
    this.viewportService.viewportSizeChanged$.pipe(takeUntil(this.destroyed$)).subscribe(data => {
      this.isMobile = data == 'sm' || data == 'xs' || data == 'md'
    })
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true)
    this.destroyed$.complete()
  }

  open(creationMode = true) {
    this.creationMode = creationMode

    this.modalService.openModal({
      body: this.content,
      title: 'Automated Questions',
      width: 'xl',
      bodyHeight: this.isMobile ? 'half' : 'large',
      showFooter: true,
      footer: this.footer,
    })
  }

  private resetCurrentAutomationOnClose() {
    this.modalService.modalClosed$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.autoQuestionStore.setCurrentAutoQuestionFormId(undefined)
    })
  }

  onSave(existingFormData: IAutoQuestionFormData | undefined) {
    this.cdr?.detectChanges() // TODO: use valueChanges in child components to update the state and avoid the need for this

    const nameFormValue = this.nameFormComponent?.validateAndReturnData?.()
    const triggerFormValue = this.triggerFormComponent?.validateAndReturnData?.()
    const questionsFormValue = this.questionsFormComponent?.validateAndReturnData?.()
    const messagesFormDValue = this.messagesFormComponent?.validateAndReturnData?.()

    if (!nameFormValue && !triggerFormValue && !questionsFormValue && !messagesFormDValue) {
      return
    }

    const formData: Partial<IAutoQuestionFormData> = {
      ...nameFormValue,
      ...triggerFormValue,
      ...questionsFormValue,
      ...messagesFormDValue,
    }

    if (!existingFormData && nameFormValue && triggerFormValue) {
      // The add resolves the company default messages asynchronously, so the save can only be
      // signalled once the new set is in the store — otherwise the parent persists the list as it
      // was before the add and the question set is silently dropped (CNCT-6792).
      this.autoQuestionStore
        .addAutoQuestionFromForm(formData as IAutoQuestionFormData)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.onSavedChanges.emit())
      return
    }

    if (!existingFormData) {
      return
    }

    this.autoQuestionStore.onUpdateCurrentAutoQuestion({ ...existingFormData, ...formData })
    this.onSavedChanges.emit()
  }

  selectedTabChanged(tab: E11TabComponent) {
    this.selectedTabId = tab.tabId as FormStep
    if (this.cdr) this.cdr.markForCheck()
  }

  get jobTitle(): string {
    return this.jobDetail?.jobTitle || ''
  }

  get jobLocation(): string {
    const city = this.jobDetail?.locationAddress?.city || ''
    const state = this.jobDetail?.locationAddress?.state || ''
    return `${city}${city && state ? ', ' : ''}${state}`
  }
}
